import { servedWorldsOf, WORLD_SLUG, type ServedWorld } from '@vrlearning/lesson-schema';

/**
 * The console's window onto a world (part 3b, spec §3; contract V9): the headset app at
 * `?worldViewer=<ticket>`, embedded by the console's library page as the model viewer is. No XR, no
 * socket, no pairing; the ticket -- single use, a minute -- fetches the world's served package and its
 * signed files. It builds the world as a headset would and draws the stage's first view: the eyes at
 * 1.6 m on the stage's middle, looking along -z, the world's features in their first states. It
 * answers the console by postMessage:
 *
 * | Console → viewer | Viewer → console |
 * | --- | --- |
 * | `{type: 'capture'}` | `{type: 'picture', jpeg: Blob}` 1600 × 900, JPEG q 0.85 |
 * | – | `{type: 'ready'}` once built; `{type: 'trouble', text}` in Dutch |
 *
 * It listens only to the console origin the ticket's answer names, and posts only to it. The picture
 * is drawn here, in the admin's browser (the worker has no GPU); the console uploads it as the
 * world's picture ("Gebruik dit beeld als plaatje").
 */

/** What `GET /v1/world-viewer/{ticket}` answers. */
export type WorldViewerAnswer = {
  readonly world: ServedWorld;
  /** Media id → signed URL, for the package's files. */
  readonly media: Readonly<Record<string, string>>;
  readonly consoleOrigin: string;
};

/** The admin's words, in Dutch and formal. */
export const WORLD_VIEWER_TEXT = {
  loading: 'De wereld wordt geladen…',
  gone: 'Deze voorvertoning is verlopen. Open de wereld opnieuw.',
  failed: 'Deze wereld kon niet worden gebouwd.',
  drawing: 'Het plaatje lukt niet in deze browser.',
  incomplete: 'Niet alle bestanden van de wereld zijn geladen. Probeer het opnieuw.',
} as const;

/**
 * The picture: 1600 × 900, the world kit's "Maak plaatje" size (ADR-059), within the 640–1920 px the
 * picture checks take and well under the 3 MB the API accepts; JPEG at 0.85.
 */
export const WORLD_PICTURE = { width: 1600, height: 900, quality: 0.85 } as const;

/** What the address says this tab is for: a ticket to redeem, or a tab that redeemed one and was reloaded. */
export type WorldViewerStart = { kind: 'ticket'; ticket: string } | { kind: 'spent' };

/** Marks the history entry a ticket was read from, as the test drive does: a reload must not boot as a headset. */
const SPENT = 'vrlearningWorldViewer';

/** Reads `?worldViewer=<ticket>` and takes it off the address at once (a used ticket is worthless). */
export function takeWorldViewerRequest(
  place: { href: string } | undefined = globalThis.location,
  history: Pick<History, 'replaceState' | 'state'> | undefined = globalThis.history,
): WorldViewerStart | undefined {
  let url: URL | undefined;
  try {
    url = new URL(place?.href ?? '');
  } catch {
    url = undefined;
  }
  const ticket = url?.searchParams.get('worldViewer');
  if (url && ticket) {
    history?.replaceState({ [SPENT]: true }, '', url.pathname);
    return { kind: 'ticket', ticket };
  }
  const state: unknown = history?.state;
  if (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[SPENT] === true
  )
    return { kind: 'spent' };
  return undefined;
}

/**
 * The console that embeds this page, when the ticket could not say (a used or refused one): the
 * ancestor's origin, else the referrer's. A failure message goes only there, never to `*`.
 */
export function parentOrigin(
  place: { ancestorOrigins?: ArrayLike<string> } | undefined = globalThis.location,
  referrer: string = globalThis.document?.referrer ?? '',
): string | undefined {
  const ancestor = place?.ancestorOrigins?.[0];
  if (ancestor && isOrigin(ancestor)) return ancestor;
  try {
    const origin = new URL(referrer).origin;
    return isOrigin(origin) ? origin : undefined;
  } catch {
    return undefined;
  }
}

export type WorldViewerOutcome =
  | { kind: 'ready'; answer: WorldViewerAnswer }
  | { kind: 'gone' }
  | { kind: 'failed'; detail: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** An origin and nothing else (`https://host:port`): postMessage's target is never `*` or a URL. */
function isOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin !== 'null' && url.origin === value;
  } catch {
    return false;
  }
}

/** Redeems the ticket, uncached. Used, expired and never issued are one 404. */
export async function fetchWorldViewer(
  ticket: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<WorldViewerOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/v1/world-viewer/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }
  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord' };
  }
  if (!isRecord(body)) return { kind: 'failed', detail: 'onvolledig antwoord' };
  const origin = body['consoleOrigin'];
  const raw = body['world'];
  // Read as the lessons' served worlds are: one entry, keyed by its slug, of the right shape.
  const slug = isRecord(raw) && typeof raw['slug'] === 'string' ? raw['slug'] : undefined;
  const world =
    slug !== undefined && WORLD_SLUG.test(slug) ? servedWorldsOf({ [slug]: raw })[slug] : undefined;
  if (!world || typeof origin !== 'string' || !isOrigin(origin)) {
    return { kind: 'failed', detail: 'onvolledig antwoord' };
  }
  const media = isRecord(body['media']) ? body['media'] : {};
  return {
    kind: 'ready',
    answer: {
      world,
      media: Object.fromEntries(
        Object.entries(media).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string',
        ),
      ),
      consoleOrigin: origin,
    },
  };
}

/** What the viewer needs drawn (render/worlds/viewer-scene.ts; a fake in tests). */
export type WorldViewerRenderer = {
  /** The world built and loaded as a headset would; throws when it cannot be. */
  load(answer: WorldViewerAnswer): Promise<void>;
  /** The stage's first view, 1600 × 900, as a JPEG; throws a `WorldIncomplete` while files are missing. */
  capture(): Promise<Blob>;
};

/** A picture refused because a file of the world is missing: it would show a world half there. */
export class WorldIncomplete extends Error {}

export type WorldViewerMessage =
  { type: 'ready' } | { type: 'trouble'; text: string } | { type: 'picture'; jpeg: Blob };

/**
 * The viewer's side of the conversation: builds the world, says `ready`, and answers a `capture` --
 * only from the console the ticket names, one request at a time.
 */
export class WorldViewerHost {
  readonly #answer: WorldViewerAnswer;
  readonly #renderer: WorldViewerRenderer;
  readonly #post: (message: WorldViewerMessage) => void;
  #built = false;
  #queue: Promise<void> = Promise.resolve();

  constructor(input: {
    answer: WorldViewerAnswer;
    renderer: WorldViewerRenderer;
    /** Posts to the console, at its origin only. */
    post: (message: WorldViewerMessage) => void;
  }) {
    this.#answer = input.answer;
    this.#renderer = input.renderer;
    this.#post = input.post;
  }

  async start(): Promise<void> {
    try {
      await this.#renderer.load(this.#answer);
    } catch {
      this.#post({ type: 'trouble', text: WORLD_VIEWER_TEXT.failed });
      return;
    }
    this.#built = true;
    this.#post({ type: 'ready' });
  }

  /** A message from the window: acted on only from the console's origin. */
  receive(event: { origin: string; data: unknown }): Promise<void> {
    if (event.origin !== this.#answer.consoleOrigin) return Promise.resolve();
    const data = event.data;
    if (!isRecord(data)) return Promise.resolve();
    this.#queue = this.#queue
      .then(() => this.#handle(data))
      .catch((error: unknown) => {
        const text =
          error instanceof WorldIncomplete
            ? WORLD_VIEWER_TEXT.incomplete
            : WORLD_VIEWER_TEXT.drawing;
        this.#post({ type: 'trouble', text });
      });
    return this.#queue;
  }

  async #handle(message: Record<string, unknown>): Promise<void> {
    if (message['type'] !== 'capture' || !this.#built) return;
    this.#post({ type: 'picture', jpeg: await this.#renderer.capture() });
  }
}
